import React from 'react';
import { useT, tr } from '../i18n';

/**
 * Form controls (spec § 2).
 *
 * Inputs are 16px, deliberately: anything smaller makes iOS Safari zoom the
 * viewport on focus, which on the show floor means fighting the browser
 * between every field. That single number is why Quick Entry is usable
 * one-handed.
 *
 * Every control is uncontrolled-friendly (`value` + `onChange`) and takes the
 * raw string — money is stored as typed (spec § 3), so nothing here parses or
 * reformats on the way in.
 */

/**
 * @param required marks the label with a red asterisk
 * @param error    message to show instead of `hint`; also turns the field red
 *
 * The red state is applied by a CLASS ON THE FIELD, not a prop on the control,
 * so `.ft-field-err .ft-in` catches Input, NumInput, Textarea and Select alike.
 * Threading an `invalid` prop through each of them instead would have meant
 * four more props and four more chances to forget one.
 *
 * The message lives inside the <label>, so a screen reader reads it out with
 * the field it belongs to rather than as a loose line of text.
 */
export function Field({ label, hint, children, className = '', required = false, error = '' }) {
  const { t } = useT();
  return (
    <label className={`ft-field ${error ? 'ft-field-err ' : ''}${className}`}>
      {label && (
        <span className="ft-label">
          {t(label)}
          {required && (
            <span className="ft-req" title={t('Required')}>
              *
            </span>
          )}
        </span>
      )}
      {children}
      {error ? (
        <span className="ft-err" role="alert">
          {t(error)}
        </span>
      ) : (
        hint && <span className="ft-hint">{t(hint)}</span>
      )}
    </label>
  );
}

/** Date-like fields where tapping the field itself should open the picker. */
const PICKER_TYPES = ['date', 'time', 'datetime-local', 'month', 'week'];

export function Input({ value = '', onChange, type = 'text', ...rest }) {
  /**
   * Open the native picker from anywhere in the field, not just the small
   * calendar glyph. On a phone at a booth that glyph is a ~16px target next
   * to a 44px field, and missing it just puts a caret in the day segment.
   *
   * `showPicker` is Chrome 99+/Safari 16+; where it is missing, or where the
   * browser refuses it (it throws without a user gesture), the field keeps
   * its normal behaviour.
   */
  const openPicker = (e) => {
    if (!PICKER_TYPES.includes(type)) return;
    const el = e.currentTarget;
    if (typeof el.showPicker !== 'function') return;
    try {
      el.showPicker();
    } catch (err) {
      /* not supported here — the glyph still works */
    }
  };

  return (
    <input
      className="ft-in"
      type={type}
      value={value == null ? '' : value}
      onChange={(e) => onChange && onChange(e.target.value)}
      onClick={openPicker}
      {...rest}
    />
  );
}

/** Money and quantities: `inputMode="decimal"` gets the numeric keypad without
 *  the spinner arrows or the browser's own number coercion. */
export function NumInput({ value = '', onChange, ...rest }) {
  return (
    <input
      className="ft-in"
      type="text"
      inputMode="decimal"
      value={value == null ? '' : value}
      onChange={(e) => onChange && onChange(e.target.value)}
      {...rest}
    />
  );
}

export function Textarea({ value = '', onChange, rows = 4, ...rest }) {
  return (
    <textarea
      className="ft-in ft-ta"
      rows={rows}
      value={value == null ? '' : value}
      onChange={(e) => onChange && onChange(e.target.value)}
      {...rest}
    />
  );
}

/**
 * `options` accepts plain strings or {value,label} pairs. An empty-string
 * option is a real choice (several spec fields are optional tri-states), so it
 * is never filtered out.
 */
export function Select({ value = '', onChange, options = [], placeholder, ...rest }) {
  const { t } = useT();
  return (
    <select
      className="ft-in ft-sel"
      value={value == null ? '' : value}
      onChange={(e) => onChange && onChange(e.target.value)}
      {...rest}
    >
      {placeholder != null && <option value="">{t(placeholder)}</option>}
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        return (
          <option key={val} value={val}>
            {label === '' ? '—' : t(label)}
          </option>
        );
      })}
    </select>
  );
}

/**
 * Segmented single choice. `tone` colours the selected item semantically —
 * used by the decision segment, where Strong/Maybe/No must read as
 * green/amber/red (spec § 2).
 */
export function Segment({ value, onChange, options = [], tone }) {
  const { t } = useT();
  const toneFor = (v) => {
    if (tone !== 'decision') return '';
    if (v === 'Strong' || v === 'APPROVED' || v === 'Verified') return ' ft-seg-green';
    if (v === 'Maybe' || v === 'HOLD' || v === 'Pending') return ' ft-seg-amber';
    if (v === 'No' || v === 'REJECTED' || v === 'Problem') return ' ft-seg-red';
    return '';
  };

  return (
    <div className="ft-seg" role="group">
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        const on = value === val;
        return (
          <button
            key={val}
            type="button"
            className={`ft-seg-item${on ? ' ft-seg-on' + toneFor(val) : ''}`}
            aria-pressed={on}
            onClick={() => onChange && onChange(val)}
          >
            {label === '' ? '—' : t(label)}
          </button>
        );
      })}
    </div>
  );
}

/** Multi-select chips — certification types, filters. */
export function Chips({ value = [], onChange, options = [] }) {
  const { t } = useT();
  const toggle = (v) => {
    const next = value.includes(v) ? value.filter((x) => x !== v) : [...value, v];
    onChange && onChange(next);
  };
  return (
    <div className="ft-chips">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={`ft-chip${value.includes(o) ? ' ft-chip-on' : ''}`}
          aria-pressed={value.includes(o)}
          onClick={() => toggle(o)}
        >
          {t(o)}
        </button>
      ))}
    </div>
  );
}

/**
 * 1–5 stars at 30px (spec § 2) — large enough to hit accurately while holding
 * a phone in one hand at a booth. Tapping the current rating clears it.
 */
export function Stars({ value = 0, onChange, readOnly = false, size = 30 }) {
  if (readOnly) {
    return (
      <span className="ft-stars ft-stars-ro" aria-label={`${value} of 5`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <span key={n} className={n <= value ? 'ft-star-on' : 'ft-star-off'}>
            ★
          </span>
        ))}
      </span>
    );
  }
  return (
    <div className="ft-stars" role="group" aria-label={tr("Rating")}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          style={{ fontSize: size }}
          className={n <= value ? 'ft-star-on' : 'ft-star-off'}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
          aria-pressed={n <= value}
          onClick={() => onChange && onChange(n === value ? 0 : n)}
        >
          ★
        </button>
      ))}
    </div>
  );
}

/** A labelled checkbox. `tone="amber"` is the revisit flag (spec § 5.4). */
export function Check({ checked = false, onChange, label, tone }) {
  const { t } = useT();
  return (
    <label className={`ft-check${checked && tone ? ` ft-check-${tone}` : ''}`}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange && onChange(e.target.checked)} />
      <span>{t(label)}</span>
    </label>
  );
}

/** Yes/No toggle for the many tri-state review fields. */
export function YesNo({ value = '', onChange }) {
  return <Segment value={value} onChange={onChange} options={['', 'Yes', 'No']} />;
}
